import { parse, TYPE, type MessageFormatElement } from "@formatjs/icu-messageformat-parser";
import type { Catalog, CatalogNode } from "./catalog.ts";
import type { ResolveLocaleInput, ResolvedLocale } from "./resolveLocale.ts";
import { parseAcceptLanguage } from "./resolveLocale.ts";

// Runtime-safe (no Sentry, no React): each app hands in its own reporter, so the same alerts, payloads and
// de-duplication hold in the consultant app and the client app.

/** The Linear issue that implements the full handling of these alerts (`edge-case-triage`). */
export const I18N_TRACKING_ISSUE = "FIN-1519";

export type I18nAlertType =
  | "i18n_missing_key"
  | "i18n_message_format_error"
  | "i18n_locale_unresolved"
  | "i18n_unsupported_locale"
  | "i18n_icu_data_missing";

export interface I18nAlert {
  errorType: I18nAlertType;
  trackingIssue: typeof I18N_TRACKING_ISSUE;
  /** The locale that was being rendered or resolved when it happened. */
  locale: string;
  /** What went wrong, for the person reading the alert. Never user data. */
  detail: string;
}

export type ReportI18nAlert = (alert: I18nAlert) => void;

/** The part of use-intl's `IntlError` this module reads. */
interface IntlErrorLike {
  code: string;
  message: string;
  originalMessage?: string;
}

export interface I18nHandlers {
  onError: (error: IntlErrorLike) => void;
  getMessageFallback: (info: { error: IntlErrorLike; key: string; namespace?: string }) => string;
}

// A render loop must not flood the reporter: one alert per distinct cause per page session / server process.
const MAX_REMEMBERED_ALERTS = 500;

/** Reports each distinct alert once; `raise` returns whether it reported. */
export function createI18nAlerts(report: ReportI18nAlert) {
  const seen = new Set<string>();

  function raise(alert: Omit<I18nAlert, "trackingIssue">): boolean {
    const identity = `${alert.errorType}|${alert.locale}|${alert.detail}`;
    if (seen.has(identity) || seen.size >= MAX_REMEMBERED_ALERTS) return false;
    seen.add(identity);
    try {
      report({ ...alert, trackingIssue: I18N_TRACKING_ISSUE });
    } catch (reportError) {
      // A broken reporter must never break rendering; the console is the last place left to say so.
      console.error("[i18n] could not report", alert.errorType, reportError);
    }
    return true;
  }

  return { raise };
}

export type I18nAlerts = ReturnType<typeof createI18nAlerts>;

function alertTypeFor(code: string): I18nAlertType {
  switch (code) {
    case "ENVIRONMENT_FALLBACK":
      return "i18n_icu_data_missing";
    case "MISSING_MESSAGE":
    case "INSUFFICIENT_PATH":
    case "INVALID_KEY":
      return "i18n_missing_key";
    default:
      return "i18n_message_format_error";
  }
}

function lookup(messages: Record<string, Catalog>, path: string): string | undefined {
  let node: CatalogNode | undefined = messages as unknown as CatalogNode;
  for (const segment of path.split(".")) {
    node = typeof node === "object" ? node[segment] : undefined;
  }
  return typeof node === "string" ? node : undefined;
}

function plainText(elements: MessageFormatElement[]): string {
  return elements
    .map((element) => {
      switch (element.type) {
        case TYPE.literal:
          return element.value;
        case TYPE.select:
        case TYPE.plural:
          return plainText((element.options.other ?? Object.values(element.options)[0]).value);
        case TYPE.tag:
          return plainText(element.children);
        default:
          // An argument whose value the fallback cannot know.
          return "…";
      }
    })
    .join("");
}

/** The English sentence for a message, readable without its values; `undefined` when English has none. */
export function englishFallbackText(english: Record<string, Catalog>, path: string): string | undefined {
  const message = lookup(english, path);
  if (message === undefined) return undefined;
  try {
    return plainText(parse(message));
  } catch {
    // tier3: English is checked by `i18n:check`; an unparseable source line shows as-is rather than not at all.
    return message;
  }
}

/**
 * `onError` / `getMessageFallback` for `NextIntlClientProvider`, `IntlProvider` and `createTranslator`: every
 * problem raises one named alert (once per cause), and the user sees the English sentence instead of a key.
 */
export function createI18nHandlers(
  alerts: I18nAlerts,
  english: Record<string, Catalog>,
  currentLocale: () => string,
): I18nHandlers {
  return {
    onError(error) {
      alerts.raise({
        errorType: alertTypeFor(error.code),
        locale: currentLocale(),
        detail: `${error.code}: ${error.originalMessage ?? error.message}`,
      });
    },
    getMessageFallback({ key, namespace }) {
      const path = namespace ? `${namespace}.${key}` : key;
      return englishFallbackText(english, path) ?? path;
    },
  };
}

/**
 * Alerts for how a locale was (not) resolved. A stored value that is not supported is `i18n_unsupported_locale`;
 * an Accept-Language that names languages we have none of, with nothing stored, is `i18n_locale_unresolved`
 * (the person gets English; the alert says which languages are being asked for).
 */
export function raiseLocaleAlerts(alerts: I18nAlerts, input: ResolveLocaleInput, resolved: ResolvedLocale): void {
  if (resolved.unsupported.length > 0) {
    alerts.raise({
      errorType: "i18n_unsupported_locale",
      locale: resolved.locale,
      detail: `unsupported stored value: ${resolved.unsupported.join(", ")}`,
    });
  }
  const offered = parseAcceptLanguage(input.acceptLanguage);
  if (resolved.source === "default" && offered.length > 0) {
    alerts.raise({
      errorType: "i18n_locale_unresolved",
      locale: resolved.locale,
      detail: `no supported language in: ${offered.join(", ")}`,
    });
  }
}
